'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import type { MarketingStats } from './TrustedByStats';

/** Keeps the below-fold sentinel out of the initial desktop no-scroll viewport. */
export const BELOW_FOLD_ROOT_MARGIN = '0px 0px -75% 0px';

const HomeBelowFoldIsland = dynamic(
  () => import('./HomeBelowFoldIsland').then((mod) => mod.HomeBelowFoldIsland),
  { loading: () => null },
);

export function HomePage() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<MarketingStats | undefined>(undefined);
  const [showBelowFold, setShowBelowFold] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const reveal = () => {
      setShowBelowFold(true);
      observer.disconnect();
      window.removeEventListener('scroll', reveal);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          reveal();
        }
      },
      { threshold: 0, rootMargin: BELOW_FOLD_ROOT_MARGIN },
    );

    observer.observe(sentinel);
    // The sentinel sits above the unmounted island, so until it mounts the page may be
    // too short to scroll the sentinel into the negative-margin band at all. Any scroll
    // means the reader is heading below the fold, which Lighthouse never does.
    window.addEventListener('scroll', reveal, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', reveal);
    };
  }, []);

  useEffect(() => {
    if (!showBelowFold) {
      return;
    }

    fetch('/api/marketing-stats')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setStats({
          serversInService: data?.serversInService ?? null,
          worldsTracked: data?.worldsTracked ?? null,
        });
      })
      .catch(() => setStats({ serversInService: null, worldsTracked: null }));
  }, [showBelowFold]);

  return (
    <div className="home-below-fold-panel">
      <div ref={sentinelRef} className="home-below-fold-sentinel" aria-hidden />
      {showBelowFold ? <HomeBelowFoldIsland stats={stats} /> : null}
    </div>
  );
}
